import {
  changeFieldValueAndUpdate,
  clearFieldValue,
  getCheckoutFormValues,
  getFormValue,
  getOfferFromSelector,
  getSection,
  getSelectedOfferInsurerIdFromSelector,
  setMessage,
  setMessageVisibility,
  setSectionLabel,
  setSectionVisibilityHelper,
} from 'app/services/businessFunctionHelpers';
import { revalidatePeselsUniqueness } from 'app/services/pesel.service/revalidatePeselsUniqueness';
import { getMessageKeyWithSuffix } from 'app/services/validation.service';
import {
  CorrespondenceAddressSelectionEnum,
  FieldsEnum,
  FormsEnum,
  InsurersEnum,
  LabelEnum,
  MessageTypeEnum,
  SectionsEnum,
  ValidationErrorMessagesEnum,
} from 'app/shared/enums';
import {
  doesPhoneNumberStartWith0,
  isPhoneNumberWithLandlinePrefix,
  isPhoneNumberWithSameDigits,
  isValidPhoneNumber,
} from 'app/validators';

const formName = FormsEnum.CheckoutForm;

const setCorrespondenceAddressSelection = () => {
  const signer = getFormValue(FieldsEnum.ContractSigner, formName);
  const ownerHomeAddressNotSameAsLegal = getFormValue(
    FieldsEnum.OwnerHomeAddressNotSameAsLegal,
    formName
  );

  const isNotInTheRegistrationCertificate = signer === 'different';

  setSectionVisibilityHelper({
    key: SectionsEnum.CheckoutSectionInsurant2,
    visible: isNotInTheRegistrationCertificate,
    formName,
  });

  if (isNotInTheRegistrationCertificate) {
    clearFieldValue(FieldsEnum.CorrespondanceAddress, formName);
  } else {
    let selection = CorrespondenceAddressSelectionEnum.OwnerLegalAddress;

    if (signer === 'owner' && ownerHomeAddressNotSameAsLegal) {
      selection = CorrespondenceAddressSelectionEnum.OwnerHomeAddress;
    } else if (signer === 'coowner1') {
      selection = CorrespondenceAddressSelectionEnum.CoOwner1LegalAddress;
    } else if (signer === 'coowner2') {
      selection = CorrespondenceAddressSelectionEnum.CoOwner2LegalAddress;
    }

    changeFieldValueAndUpdate(
      FieldsEnum.CorrespondanceAddress,
      selection,
      formName
    );

    setSectionVisibilityHelper({
      key: SectionsEnum.CheckoutSectionInsurant3,
      visible: false,
      formName,
    });

    setSectionVisibilityHelper({
      key: SectionsEnum.CheckoutSectionInsurant32,
      visible: false,
      formName,
    });
  }
};

const setOwnerAdditionalSection = () => {
  const section = getSection(
    SectionsEnum.CheckoutSectionInsurant4Owner,
    formName
  );
  const name = getFormValue(FieldsEnum.CheckoutOwnerName, formName) || '';
  const lastName =
    getFormValue(FieldsEnum.CheckoutOwnerLastName, formName) || '';
  const signer = getFormValue(FieldsEnum.ContractSigner, formName);

  const isMainOwner = signer === 'owner';

  if (!section) {
    return;
  }

  setSectionLabel({
    key: SectionsEnum.CheckoutSectionInsurant4Owner,
    label: `${LabelEnum.Owner} - ${name} ${lastName}`,
    formName,
  });

  setSectionVisibilityHelper({
    key: SectionsEnum.CheckoutSectionInsurant4Owner,
    visible: !isMainOwner,
    formName,
  });
};

const setupInsurant = {
  setupInsurant() {
    const signer = getFormValue(FieldsEnum.ContractSigner, formName);
    const offer = getOfferFromSelector();

    const isNotInTheRegistrationCertificate = signer === 'different';
    const isLink4Offer = offer?.insurerName === InsurersEnum.Link4;
    const isOminimoOffer = offer?.insurerName === InsurersEnum.Ominimo;

    setSectionVisibilityHelper({
      key: SectionsEnum.CheckoutSectionInsurant1,
      visible: isNotInTheRegistrationCertificate,
      formName,
    });

    if (isLink4Offer || isOminimoOffer) {
      setCorrespondenceAddressSelection();
      setOwnerAdditionalSection();
    }

    revalidatePeselsUniqueness(formName);
  },
  onInsurantPhoneNumberChange(initialRun?: boolean) {
    const { phoneNumber } = getCheckoutFormValues();
    const selectedOfferInsurerId = getSelectedOfferInsurerIdFromSelector();
    const isValid = isValidPhoneNumber(phoneNumber);

    const insurersWithExtendedValidation = [
      InsurersEnum.Euroins,
      InsurersEnum.Allianz,
      InsurersEnum.Hestia,
      InsurersEnum.ErgoHestia,
      InsurersEnum.Mtu24,
      InsurersEnum.YouCanDrive,
    ];

    const isInsurerWithExtendedValidation = insurersWithExtendedValidation.includes(
      selectedOfferInsurerId
    );

    if (isInsurerWithExtendedValidation) {
      setMessage({
        formName,
        message: {
          content: ValidationErrorMessagesEnum.InvalidMobilePhoneNumber,
          key: getMessageKeyWithSuffix(
            FieldsEnum.CheckoutInsurantPhone,
            'invalid_phone_number'
          ),
          type: MessageTypeEnum.Danger,
          visible: false,
        },
      });
    }

    if (!isValid) return;

    const startsWith0 = doesPhoneNumberStartWith0(phoneNumber);
    const sameDigits = isPhoneNumberWithSameDigits(phoneNumber);
    const withLandlinePrefix = isPhoneNumberWithLandlinePrefix(phoneNumber);

    if (isInsurerWithExtendedValidation) {
      setMessageVisibility({
        key: getMessageKeyWithSuffix(
          FieldsEnum.CheckoutInsurantPhone,
          'invalid_mobile_phone_number'
        ),
        visible: startsWith0 || withLandlinePrefix || sameDigits,
        formName,
      });
    }

    setMessageVisibility({
      key: getMessageKeyWithSuffix(
        FieldsEnum.CheckoutInsurantPhone,
        'invalid_mobile_phone_number_default'
      ),
      visible: sameDigits || startsWith0,
      formName,
    });
  },
};

export default setupInsurant;
